















































































import { Component, Mixins, Watch } from "vue-property-decorator";
import BreakdownTable, {
  BreakdownTableFilter,
  BreakdownTotals,
} from "@/components/Investor/BreakdownTable.vue";
import SpotsMixin from "@/mixins/http/Spot";
import EntryMixin from "@/mixins/http/Entry";
import InvestorMixin from "@/mixins/http/Investor";
import { AdcontrolProfile, Product, Spot, Target, User } from "@/models";
import Compressor from "@/services/Compressor";

import DatePicker, { DATE_FORMAT } from "@/components/DatePicker.vue";
import { Getter } from "vuex-class";
import MediaValue from "@/interfaces/MediaValue";
import StickyPlayer from "@/components/Spot/StickyPlayer.vue";
import VPageContentSticky from "@/components/VPageContentSticky.vue";
import EntryItem from "@/components/EntryItem.vue";
import MediaSelect from "@/components/MediaSelect.vue";
import DaysComparationsCard from "@/components/Investor/DaysComparationsCard.vue";
import { BreakdownSummaryTotals } from "../Summary.vue";
import QueryHelper from "@/services/QueryHelper";
import QueryParser from "@/services/QueryParser";
import WithTimeIntervals from "@/models/interfaces/WithTimeIntervals";
import { FEATURE_AUDITEL } from "@/constants";
import GrpTargetSelect from "@/components/GrpTargetSelect.vue";
import MultiLevelObject from "@/interfaces/MultiLevelObject";

const requestTag = "distributions";

export interface InvestorSpotFilter {
  unitOfMeasure: string;
  agencies: boolean;
  media: MediaValue;
  date: {
    from: string;
    to: string;
  };
}

Component.registerHooks(["beforeRouteUpdate"]);

@Component({
  components: {
    EntryItem,
    StickyPlayer,
    BreakdownTable,
    VPageContentSticky,
    MediaSelect,
    DatePicker,
    DaysComparationsCard,
    GrpTargetSelect,
  },
})
export default class CreativityDetail extends Mixins(
  SpotsMixin,
  EntryMixin,
  InvestorMixin
) {
  @Getter("lastDate") readonly lastDate!: string;
  @Getter("userMedia") readonly userMedia!: MediaValue;
  @Getter("user") readonly user!: User;
  @Getter readonly parseDateValue!: (
    date: string,
    format: string,
    defaultOffset?: number,
    defaultUnit?: any
  ) => string;
  @Getter readonly parseAndCleanMediaValue!: (
    radio: string | boolean | undefined,
    tv: string | boolean | undefined
  ) => MediaValue;
  @Getter readonly parseOneZeroToBoolean!: (
    oneZero: string | (string | null)[]
  ) => boolean;

  ids: string[] = [];
  productId = "";
  ready = false;
  spots: Spot[] = [];
  weekdaysComparisons: (Spot & WithTimeIntervals)[] = [];
  hoursComparisons: (Spot & WithTimeIntervals)[] = [];

  buildFilterFromQuery(): InvestorSpotFilter {
    const query = this.$route.query;
    const { radio, tv } = query;

    const from = this.parseDateValue(
      query.from as string,
      DATE_FORMAT,
      3,
      "months"
    );
    const to = this.parseDateValue(query.to as string, DATE_FORMAT);

    const media = this.parseAndCleanMediaValue(
      QueryParser.queryToBoolean(radio),
      QueryParser.queryToBoolean(tv)
    );

    const unitOfMeasure = (query.unit as string) ?? "spots";
    const agencies = this.parseOneZeroToBoolean(query.agencies);

    return {
      media,
      date: {
        from,
        to,
      },
      unitOfMeasure,
      agencies,
    };
  }

  @Watch("filter", { deep: true })
  onFilterChanged() {
    if (this.filter.media.radio) {
      if (
        this.filter.unitOfMeasure == "grp" ||
        this.filter.unitOfMeasure == "grp_over_spots" ||
        this.filter.unitOfMeasure == "audience"
      ) {
        this.filter.unitOfMeasure = "spots";
      }
    }

    if (QueryHelper.isSameQuery(this.$route.query, this.queryFilter)) {
      return;
    }

    this.$router.replace({ query: { ...this.queryFilter } });
  }

  get queryFilter(): Dictionary<string> {
    const query = {
      from: this.filter.date.from as string,
      to: this.filter.date.to as string,
      tv: this.filter.media.tv ? "1" : "0",
      radio: this.filter.media.radio ? "1" : "0",
      unit: this.filter.unitOfMeasure ?? "spots",
      agencies: this.filter.agencies ? "1" : "0",
    };
    return query;
  }

  created() {
    this.filter = { ...this.buildFilterFromQuery() };
    this.productId = this.$route.params.product_id;
    this.ids = Compressor.decompress(this.$route.params.ids) || [];
  }

  get titleSuffix() {
    return `(${this.spots.length} creatività)`;
  }

  async load() {
    await Promise.all([this.loadEntry(), this.loadSpots()]);
    this.ready = true;
  }

  async loadEntry() {
    this.loading.entry = true;
    this.entry = await this.getProduct(
      this.$route.params.product_id,
      requestTag
    );
    this.loading.entry = false;
  }

  async loadSpots() {
    this.loading.spots = true;
    this.spots = await this.getSpots(
      { page: { size: "all" }, filter: { ids: [...this.ids] } },
      requestTag
    );
    this.loading.spots = false;
  }

  entry: Product | null = null;

  currentGrpTarget: Target | null = null;

  chartEntries = [];

  totals: BreakdownTotals | null = null;

  loading = {
    entry: false,
    spots: false,
    distributions: false,
    inlineComparisons: false,
  };

  filter: InvestorSpotFilter = {
    unitOfMeasure: "spots",
    agencies: false,
    media: {
      radio: true,
      tv: true,
    },
    date: {
      from: "",
      to: "",
    },
  };

  mounted() {
    this.filter = { ...this.buildFilterFromQuery() };
    this.load();
  }

  get distributionTableFilter(): BreakdownTableFilter {
    return {
      spots: this.ids,
      date: this.filter.date,
      media: this.filter.media,
      ready: this.ready,
      entry: this.entry,
      grouped: this.filter.agencies,
    };
  }

  get daysComparationsFilter() {
    return {
      date: { ...this.filter.date },
      media: { ...this.filter.media },
    };
  }

  get entity() {
    return {
      type: Product.type,
      id: this.productId,
    };
  }

  get hasAuditel(): boolean {
    return this.user.hasFeature(FEATURE_AUDITEL);
  }

  get grpTargetId() {
    return this.currentGrpTarget?.id;
  }

  get unitOfMeasureOptions(): Array<MultiLevelObject> {
    const grpOptions: Array<MultiLevelObject> = [];
    if (this.hasAuditel && !this.filter.media.radio) {
      grpOptions.push(
        {
          id: "grp",
          text: "GRP",
        },
        {
          id: "grp_over_spots",
          text: "GRP / Spot",
        },
        {
          id: "audience",
          text: "Contatti",
        }
      );
    }
    return [
      {
        id: "spots",
        text: "Spot",
      },
      {
        id: "seconds",
        text: "Secondi",
      },
      ...grpOptions,
    ];
  }

  onSettingsChanged(settings: { unitOfMeasure: string; grouped: boolean }) {
    this.filter.unitOfMeasure = settings.unitOfMeasure;
    this.filter.agencies = settings.grouped;
  }

  onComparisonSettingsChanged(settings: { unitOfMeasure: string }) {
    this.filter.unitOfMeasure = settings.unitOfMeasure;
  }

  onGroupedChange(newValue: boolean) {
    this.filter.agencies = newValue;
  }

  onTargetSelected(target: Target) {
    this.currentGrpTarget = target;
  }

  protected onDistributionLoading() {
    this.loading.distributions = true;
  }

  protected onDistributionLoaded(totals: BreakdownSummaryTotals) {
    this.totals = totals;
    this.loading.distributions = false;
  }

  protected onDateSelected(data: { range: { from: string; to: string } }) {
    if (data.range) {
      this.filter.date = data.range;
    }
  }
}
